// ── GQ™ Optimize v2 — Evolution viz, population, detailed results ─────

const OPT_STRATS = ["BTC Momentum","ETH Alpha","SOL Breakout","Gold Trend","MA Crossfilter"];
const PARAM_RANGES = [
  {name:"Fast period",min:5,max:50,step:1,best:9},
  {name:"Slow period",min:20,max:200,step:5,best:21},
  {name:"RSI period",min:5,max:30,step:1,best:14},
  {name:"RSI threshold",min:40,max:60,step:1,best:52},
  {name:"Stop loss %",min:1,max:10,step:0.5,best:3.5},
];
const OBJECTIVES = ["Sharpe","Sortino","CAGR","Max DD (minimize)","Custom weighted"];

// Fitness color: purple→blue→green
function fitColor(t,a){
  let r,g,b;
  if(t<0.5){const s=t/0.5;r=139+(59-139)*s;g=92+(158-92)*s;b=246+(255-246)*s;}
  else{const s=(t-0.5)/0.5;r=59+(17-59)*s;g=158+(255-158)*s;b=255+(153-255)*s;}
  return`rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${a})`;
}

// ── Scatter Plot (animated population) ────────────────────────────────
function EvolutionScatter({candidates,gen,total,height=300}){
  const ref=React.useRef(null);
  React.useEffect(()=>{
    if(!ref.current||!window.Plotly||candidates.length===0)return;
    // Group by generation
    const gens=[...new Set(candidates.map(c=>c.gen))].sort((a,b)=>a-b);
    const traces=gens.map(g=>{
      const pts=candidates.filter(c=>c.gen===g);
      return{x:pts.map(p=>p.gen),y:pts.map(p=>p.fitness*100),
        type:"scatter",mode:"markers",name:`Gen ${g}`,showlegend:false,
        marker:{size:pts.map(p=>p.best?10:5),color:pts.map(p=>fitColor(p.fitness,0.8)),
          line:{color:pts.map(p=>p.best?"rgba(255,255,255,0.4)":"transparent"),width:pts.map(p=>p.best?1.5:0)}},
        hovertemplate:"Gen %{x}<br>Fitness <b>%{y:.1f}%</b><extra></extra>",
      };
    });
    // Frontier line
    const bests=[];gens.forEach(g=>{const pts=candidates.filter(c=>c.gen===g);const top=pts.reduce((a,b)=>a.fitness>b.fitness?a:b);bests.push({x:g,y:top.fitness*100});});
    if(bests.length>1)traces.push({x:bests.map(b=>b.x),y:bests.map(b=>b.y),type:"scatter",mode:"lines",name:"Best fitness",line:{color:"#11ff99",width:2,shape:"spline"},showlegend:false});
    window.Plotly.newPlot(ref.current,traces,{
      paper_bgcolor:"rgba(0,0,0,0)",plot_bgcolor:"rgba(0,0,0,0)",
      font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},
      margin:{l:45,r:15,t:15,b:35},height,
      xaxis:{gridcolor:"rgba(255,255,255,0.05)",title:{text:"Generation",font:{size:10,color:"rgba(245,245,247,0.4)"}},range:[0,total+1],tickfont:{family:"Geist Mono,monospace",size:9,color:"rgba(245,245,247,0.5)"}},
      yaxis:{gridcolor:"rgba(255,255,255,0.05)",ticksuffix:"%",title:{text:"Fitness",font:{size:10,color:"rgba(245,245,247,0.4)"}},tickfont:{family:"Geist Mono,monospace",size:9,color:"rgba(245,245,247,0.5)"}},
    },{displayModeBar:false,responsive:true});
    return()=>{try{window.Plotly.purge(ref.current)}catch{}};
  },[candidates,gen]);
  return<div ref={ref} style={{width:"100%",height}}/>;
}

// ── Diversity meter ───────────────────────────────────────────────────
function DiversityMeter({value}){
  const pct=Math.round(value*100);
  const c=value>.6?"#11ff99":value>.3?"#3b9eff":"#D946EF";
  return(
    <div style={{display:"flex",alignItems:"center",gap:10}}>
      <div style={{flex:1,height:6,background:"rgba(255,255,255,0.04)",borderRadius:9999,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${pct}%`,background:c,borderRadius:9999,transition:"width .3s"}}/>
      </div>
      <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:c,minWidth:36}}>{pct}%</span>
    </div>
  );
}

// ── Main Optimize Page ────────────────────────────────────────────────
function OptimizePage({onToast,onNavigate}){
  const [strategy,setStrategy]=React.useState(OPT_STRATS[0]);
  const [objective,setObjective]=React.useState("Sharpe");
  const [running,setRunning]=React.useState(false);
  const [gen,setGen]=React.useState(0);
  const [done2,setDone2]=React.useState(false);
  const [ranges,setRanges]=React.useState(PARAM_RANGES.map(p=>({...p,lo:p.min,hi:p.max})));
  const [candidates,setCandidates]=React.useState([]);
  const [topCandidates,setTopCandidates]=React.useState([]);
  const [diversity,setDiversity]=React.useState(1);
  const [aiSummary,setAiSummary]=React.useState(null);
  const [resultTab,setResultTab]=React.useState("params");
  const tRef=React.useRef(null);
  const TOTAL=40;

  const [paretoFront, setParetoFront] = React.useState([]);
  const [bestParams, setBestParams] = React.useState(null);
  const [sensReport, setSensReport] = React.useState(null);   // {baseFitness, sensitivity:{key:[{pct,fitness,delta}]}}
  const [robust, setRobust] = React.useState(null);            // {score, neighbourMean, neighbourStd, samples:[]}
  const [algorithm, setAlgorithm] = React.useState("GA");      // GA | NSGA-II | Bayesian
  const [pboScore, setPboScore] = React.useState(null);        // 0–1, computed after run
  const [pboLoading, setPboLoading] = React.useState(false);
  const [beforeMetrics, setBeforeMetrics] = React.useState(null);
  const [afterMetrics, setAfterMetrics] = React.useState(null);

  const start = async () => {
    setRunning(true); setDone2(false); setGen(0);
    setCandidates([]); setTopCandidates([]); setDiversity(1); setAiSummary(null);
    setParetoFront([]); setBestParams(null); setPboScore(null); setBeforeMetrics(null); setAfterMetrics(null);
    const allCands = [];

    try {
      const { engine, data, state, optimize, stats } = window.GQ;
      const bars = await data.load('SPY', { bars: 1260 });
      // Build param-range spec for the GA
      const gaRanges = {
        fast:   [ranges[0].lo, ranges[0].hi, 'int'],
        slow:   [ranges[1].lo, ranges[1].hi, 'int'],
        rsiP:   [ranges[2].lo, ranges[2].hi, 'int'],
        rsiT:   [ranges[3].lo, ranges[3].hi, 'int'],
        stopL:  [ranges[4].lo, ranges[4].hi, 'float'],
      };
      const makeStrat = (p) => ({
        indicators: [
          { id:'ef', type:'ema', source:'close', params:{period:p.fast} },
          { id:'es', type:'ema', source:'close', params:{period:Math.max(p.fast+1, p.slow)} },
          { id:'rsi', type:'rsi', source:'close', params:{period:p.rsiP} },
          { id:'atr', type:'atr', source:'close', params:{period:14} },
        ],
        entry: { long: [`ef > es and rsi > ${p.rsiT}`] },
        exit:  { long: ['ef < es'] },
        sizing: { mode:'atrTarget', atrK:2, riskPct: p.stopL / 100 },
        execution: { orderType:'market' },
        costs: { commissionBps:5, slippageBps:2 },
        constraints: { allowShort:false },
      });

      // Pick fitness from objective dropdown
      const fitnessKey = ({
        'Sharpe':'sharpe', 'Sortino':'sortino', 'CAGR':'cagr', 'Max DD (minimize)':'_negDD', 'Custom weighted':'_combo',
      })[objective] || 'sharpe';
      const fitness = async (p) => {
        if (p.fast >= p.slow) return -10;
        const r = engine.run(makeStrat(p), bars, { capital:10000, seed:42 });
        const m = r.metrics;
        if (fitnessKey === '_negDD')  return -(m.maxDrawdown || 1);
        if (fitnessKey === '_combo')  return (m.sharpe || 0) - 2 * (m.maxDrawdown || 0);
        return m[fitnessKey] || 0;
      };

      const TOTAL_GENS = 30;
      const POP = 16;

      // Algorithm selector gates the optimization loop
      if (algorithm === 'NSGA-II' || objective === 'Custom weighted') {
        const result = await optimize.nsga2({
          ranges: gaRanges,
          fitnessMulti: async (p) => {
            if (p.fast >= p.slow) return [-10, 1];
            const r = engine.run(makeStrat(p), bars, { capital:10000, seed:42 });
            return [r.metrics.sharpe || 0, r.metrics.maxDrawdown || 1];
          },
          objectives: ['max', 'min'],
          generations: TOTAL_GENS, populationSize: POP, seed: 42,
          onGeneration: ({ gen: g, population, objectives: vecs }) => {
            setGen(g + 1);
            const popCands = population.map((p, i) => ({
              gen: g + 1, fitness: Math.max(0, Math.min(1, (vecs[i][0] + 0.5) / 4)),
              best: false, id: `${g}-${i}`, params: Object.values(p), raw: vecs[i],
            }));
            allCands.push(...popCands);
            setCandidates([...allCands]);
            setTopCandidates([...allCands].sort((a,b) => b.fitness - a.fitness).slice(0, 10));
            setDiversity(Math.max(0.1, 1 - g / TOTAL_GENS * 0.6));
          },
        });
        setParetoFront(result.paretoFront);
        setBestParams(result.paretoFront[0]?.params);
      } else {
        let result;
        if (algorithm === 'Bayesian') {
          const ITERATIONS = 30, INITIAL = 8;
          let bestSoFar = -Infinity;
          const synthHist = [];
          const bres = await optimize.bayesianOptimize({
            ranges: gaRanges, fitness,
            iterations: ITERATIONS, initialPoints: INITIAL, seed: 42,
            onIteration: ({ iter, candidate, fitness: f }) => {
              bestSoFar = Math.max(bestSoFar, f);
              const g = iter + 1;
              setGen(g);
              const cand = {
                gen: g, fitness: Math.max(0, Math.min(1, (f + 1) / 4)),
                best: false, id: `bo-${iter}`, params: Object.values(candidate),
              };
              allCands.push(cand);
              setCandidates([...allCands]);
              setTopCandidates([...allCands].sort((a,b) => b.fitness - a.fitness).slice(0, 10));
              synthHist.push({ gen: g, best: bestSoFar, mean: f });
              setDiversity(Math.max(0.1, 0.8 - g / ITERATIONS * 0.5));
            },
          });
          result = { bestParams: bres.bestParams, bestFitness: bres.bestFitness, history: synthHist };
        } else {
          result = await optimize.geneticAlgorithm({
            ranges: gaRanges, fitness,
            generations: TOTAL_GENS, populationSize: POP, seed: 42,
            onGeneration: ({ gen: g, population, fitness: fits, bestIndividual }) => {
              setGen(g + 1);
              const popCands = population.map((p, i) => ({
                gen: g + 1, fitness: Math.max(0, Math.min(1, (fits[i] + 1) / 4)),
                best: i === 0, id: `${g}-${i}`, params: Object.values(p),
              }));
              allCands.push(...popCands);
              setCandidates([...allCands]);
              setTopCandidates([...allCands].sort((a,b) => b.fitness - a.fitness).slice(0, 10));
              setDiversity(Math.max(0.1, fits[0] > 0 ? stats.stdev(fits) / Math.abs(stats.mean(fits) + 1e-6) : 0.5));
            },
          });
        }
        setBestParams(result.bestParams);

        // Compute before/after metrics for the params panel
        try {
          const defaultP = { fast:9, slow:21, rsiP:14, rsiT:52, stopL:3.5 };
          const bR = engine.run(makeStrat(defaultP), bars, { capital:10000, seed:42 });
          setBeforeMetrics(bR.metrics);
          if (result.bestParams) {
            const aR = engine.run(makeStrat(result.bestParams), bars, { capital:10000, seed:42 });
            setAfterMetrics(aR.metrics);
          }
        } catch (e) { console.warn('[Optimize] before/after metrics', e); }

        // PBO (probability of backtest overfitting) — top 8 candidates × 8 time slices
        try {
          setPboLoading(true);
          // Recover top-8 candidate params; we kept their raw arrays in `allCands`
          const paramKeys = Object.keys(gaRanges);
          const arrToObj = (arr) => Object.fromEntries(paramKeys.map((k, i) => [k, arr[i]]));
          const seen = new Set();
          const top8 = [];
          [...allCands].sort((a,b) => b.fitness - a.fitness).forEach(c => {
            const key = c.params.join(',');
            if (!seen.has(key) && top8.length < 8) { seen.add(key); top8.push(arrToObj(c.params)); }
          });
          if (top8.length >= 2) {
            const N = bars.length, S = 8, sliceLen = Math.floor(N / S);
            const matrix = [];
            for (const p of top8) {
              const row = [];
              for (let s = 0; s < S; s++) {
                const seg = bars.slice(s * sliceLen, (s === S - 1) ? N : (s + 1) * sliceLen);
                if (seg.length < 30) { row.push(0); continue; }
                if (p.fast >= p.slow) { row.push(-1); continue; }
                const r = engine.run(makeStrat(p), seg, { capital: 10000, seed: 42 });
                row.push(r.metrics?.sharpe || 0);
              }
              matrix.push(row);
            }
            const score = optimize.pbo(matrix, S);
            setPboScore(score);
          }
        } catch (e) { console.warn('[Optimize] PBO failed', e); }
        finally { setPboLoading(false); }

        // Real sensitivity analysis on the optimal point — ±5/10/20% per param
        try {
          const sens = await optimize.sensitivityAnalysis({
            baseParams: result.bestParams, ranges: gaRanges, fitness,
            perturbations: [-0.20, -0.10, -0.05, 0.05, 0.10, 0.20],
          });
          setSensReport(sens);
        } catch (e) { console.warn('[Optimize] sensitivity failed', e); }

        // Real robustness score — re-evaluate at 20 nearby random points
        try {
          const rob = await optimize.robustnessScore({
            baseParams: result.bestParams, ranges: gaRanges, fitness,
            samples: 20, neighborhood: 0.10, seed: 7,
          });
          setRobust(rob);
        } catch (e) { console.warn('[Optimize] robustness failed', e); }

        // Persist + AI summary
        const strat = state?.getCurrentStrategy();
        if (strat?.id) {
          await state.saveOptimizationRun({
            strategy_id: strat.id,
            base_backtest_id: state.getLastBacktestRunId(),
            objective: fitnessKey,
            generations: TOTAL_GENS, population_size: POP,
            best_params: result.bestParams,
            best_metrics: { bestFitness: result.bestFitness, ...(engine.run(makeStrat(result.bestParams), bars, { capital:10000, seed:42 }).metrics) },
            history: result.history,
          });
        }

        // Real AI summary
        const sysPrompt = `You are GQ AI summarising a genetic-algorithm optimisation run.
Strategy: ${strategy}  |  Objective: ${objective}
Generations: ${TOTAL_GENS}  |  Population: ${POP}
Best params: ${JSON.stringify(result.bestParams)}
Best ${objective}: ${result.bestFitness.toFixed(3)}
Convergence history (best/gen): ${result.history.map(h => h.best.toFixed(2)).join(', ')}

In 4 bullets:
• What converged and at which generation
• What the best params mean intuitively
• Robustness check (look at convergence stability)
• Recommended next step in the pipeline (stress test / walk-forward / deploy)
Under 130 words.`;
        try {
          if (window.claude) {
            const ai = await window.claude.complete(sysPrompt);
            setAiSummary(ai);
          } else {
            setAiSummary(`Converged at Gen ${TOTAL_GENS} with best ${objective}=${result.bestFitness.toFixed(2)}. Best params: ${Object.entries(result.bestParams).map(([k,v]) => `${k}=${v}`).join(', ')}. Next: stress-test these parameters.`);
          }
        } catch (e) {
          setAiSummary("Optimization complete. AI summary unavailable.");
        }
      }
      setRunning(false); setDone2(true);
      onToast?.("Optimization converged");
      window.gqTrack?.('run_optimization', {
        algorithm, objective,
        best_fitness: bestParams ? (window.GQ?.state?.getLastOptimizationRun?.()?.best_metrics?.bestFitness) : null,
        pbo: pboScore,
      });
    } catch (e) {
      console.error('[Optimize.start]', e);
      onToast?.('Optimization failed: ' + e.message);
      setRunning(false);
    }
  };

  React.useEffect(()=>()=>clearTimeout(tRef.current),[]);

  const progress=gen/TOTAL;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <PageHdr icon={Cpu} iconColor="var(--primary)" iconBg="var(--primary-dim)"
        title="GQ™ Optimize" subtitle="Genetic algorithm parameter search — evolve your edge."
        action={<div style={{display:"flex",gap:8,alignItems:"center"}}>
          {window.PipelineContextChip && <PipelineContextChip page="optimize"/>}
          <button className="btn btn-primary" onClick={running?undefined:start} style={{opacity:running?.7:1}}>
            {running?<><RefreshCw size={11} style={{animation:"spin 1s linear infinite"}}/> Evolving gen {gen}/{TOTAL}…</>:<><GitMerge size={11}/> Run optimization</>}
          </button>
        </div>}
      />

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        {/* Config */}
        <div className="card">
          <div className="card-title">Configuration</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
            <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Strategy</div>
              <select value={strategy} onChange={e=>setStrategy(e.target.value)} className="input" style={{cursor:"pointer"}}>{OPT_STRATS.map(s=><option key={s}>{s}</option>)}</select></div>
            <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Algorithm</div>
              <select value={algorithm} onChange={e=>setAlgorithm(e.target.value)} className="input" style={{cursor:"pointer"}}>
                <option value="GA">Genetic algorithm</option>
                <option value="Bayesian">Bayesian (GP + EI)</option>
                <option value="NSGA-II">NSGA-II (Pareto)</option>
              </select></div>
            <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Objective</div>
              <select value={objective} onChange={e=>setObjective(e.target.value)} className="input" style={{cursor:"pointer"}}>{OBJECTIVES.map(o=><option key={o}>{o}</option>)}</select></div>
          </div>
          <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)",marginBottom:10,lineHeight:1.5}}>
            {algorithm === "Bayesian" && "Gaussian-process surrogate with expected-improvement acquisition. Faster convergence on expensive fitness landscapes; 8 initial + 30 iterations."}
            {algorithm === "GA" && "Population-based evolution with crossover & mutation. 16 individuals × 30 generations."}
            {algorithm === "NSGA-II" && "Multi-objective Pareto search (Sharpe ↑ vs Drawdown ↓). Use to surface the Sharpe-vs-DD trade-off frontier."}
          </div>
          <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:8,fontWeight:500}}>PARAMETER SEARCH RANGES</div>
          {ranges.map((p,i)=>(
            <div key={p.name} style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                <span style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--body)"}}>{p.name}</span>
                <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>{p.lo} — {p.hi}</span>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <input type="number" value={p.lo} onChange={e=>setRanges(prev=>prev.map((r,j)=>j===i?{...r,lo:+e.target.value}:r))} className="input" style={{width:60,padding:"4px 8px",fontFamily:"var(--font-mono)",fontSize:11}}/>
                <div style={{flex:1,height:20,background:"rgba(255,255,255,0.04)",borderRadius:"var(--r-full)",position:"relative",display:"flex",alignItems:"center"}}>
                  <div style={{height:3,background:"linear-gradient(90deg,#8b5cf6,#3b9eff,#11ff99)",opacity:0.5,borderRadius:"var(--r-full)",position:"absolute",left:`${((p.lo-p.min)/(p.max-p.min))*100}%`,width:`${((p.hi-p.lo)/(p.max-p.min))*100}%`}}/>
                  {done2&&<div style={{width:8,height:8,borderRadius:"50%",background:"#11ff99",position:"absolute",left:`${((p.best-p.min)/(p.max-p.min))*100}%`,transform:"translateX(-50%)",boxShadow:"0 0 8px rgba(17,255,153,0.6)"}}/>}
                </div>
                <input type="number" value={p.hi} onChange={e=>setRanges(prev=>prev.map((r,j)=>j===i?{...r,hi:+e.target.value}:r))} className="input" style={{width:60,padding:"4px 8px",fontFamily:"var(--font-mono)",fontSize:11}}/>
              </div>
            </div>
          ))}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,paddingTop:12,borderTop:"1px solid var(--border)"}}>
            {[["Population","200"],["Generations",String(TOTAL)],["Mutation %","5"],["Crossover","0.8"]].map(([l,v])=>(
              <div key={l}><div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)",marginBottom:3}}>{l}</div><input className="input" defaultValue={v} style={{padding:"4px 8px",fontFamily:"var(--font-mono)",fontSize:11}}/></div>
            ))}
          </div>
        </div>

        {/* Evolution viz */}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div className="card">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <span style={{fontFamily:"var(--font-ui)",fontSize:14,fontWeight:600,color:"var(--ink)"}}>Evolution scatter</span>
              <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>{running?`GEN ${gen}/${TOTAL}`:done2?"CONVERGED":"WAITING"}</span>
            </div>
            <div style={{height:3,background:"rgba(255,255,255,0.04)",borderRadius:"var(--r-full)",marginBottom:10,overflow:"hidden"}}>
              <div style={{height:"100%",background:"linear-gradient(90deg,#8b5cf6,#3b9eff,#11ff99)",width:`${progress*100}%`,transition:"width .1s",borderRadius:"var(--r-full)"}}/>
            </div>
            {candidates.length>0?<EvolutionScatter candidates={candidates} gen={gen} total={TOTAL} height={260}/>:
              <div style={{height:260,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--font-ui)",fontSize:12,color:"var(--subtle)"}}>Run optimization to see evolution</div>}
          </div>

          {/* Population detail */}
          {candidates.length>0&&<div className="card">
            <div className="card-title" style={{display:"flex",justifyContent:"space-between"}}>
              <span>Population · Top 10</span>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)"}}>Diversity</span>
                <div style={{width:80}}><DiversityMeter value={diversity}/></div>
              </div>
            </div>
            <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:"1px solid var(--border)"}}>
              {["Rank","Gen","Fitness",...ranges.map(r=>r.name)].map(h=><th key={h} style={{fontFamily:"var(--font-ui)",fontSize:9,color:"var(--muted)",textAlign:"left",padding:"3px 6px",fontWeight:500}}>{h}</th>)}
            </tr></thead><tbody>
              {topCandidates.slice(0,10).map((c,i)=>(
                <tr key={c.id} style={{borderBottom:"1px solid rgba(255,255,255,0.03)"}}>
                  <td style={{fontFamily:"var(--font-mono)",fontSize:10,color:i===0?"var(--green)":"var(--muted)",padding:"4px 6px",fontWeight:i===0?600:400}}>{i+1}</td>
                  <td style={{fontFamily:"var(--font-mono)",fontSize:10,color:"var(--muted)",padding:"4px 6px"}}>{c.gen}</td>
                  <td style={{padding:"4px 6px"}}><span style={{fontFamily:"var(--font-mono)",fontSize:10,color:fitColor(c.fitness,1),fontWeight:500}}>{(c.fitness*100).toFixed(1)}%</span></td>
                  {c.params.map((v,pi)=><td key={pi} style={{fontFamily:"var(--font-mono)",fontSize:10,color:"var(--body)",padding:"4px 6px"}}>{v.toFixed(ranges[pi]?.step<1?1:0)}</td>)}
                </tr>
              ))}
            </tbody></table></div>
          </div>}
        </div>
      </div>

      {/* Completed results */}
      {done2&&<>
        <div style={{display:"flex",gap:0,borderBottom:"1px solid var(--border)",overflowX:"auto"}}>
          {[["params","Optimal params"],["pareto","Pareto frontier"],["parallel","Parallel coords"],["sensitivity","Sensitivity"],["robustness","Robustness"],["pbo","PBO"],["summary","AI Summary"]].map(([id,label])=>(
            <button key={id} onClick={()=>setResultTab(id)} style={{background:"none",border:"none",borderBottom:`2px solid ${resultTab===id?"#D946EF":"transparent"}`,padding:"8px 16px",cursor:"pointer",fontFamily:"var(--font-ui)",fontSize:12,fontWeight:resultTab===id?600:400,color:resultTab===id?"#D946EF":"var(--muted)",transition:"all .15s",whiteSpace:"nowrap"}}>{label}</button>
          ))}
        </div>

        {resultTab==="pareto"&&<div className="card">
          <div className="card-title" style={{display:"flex",justifyContent:"space-between"}}>
            <span>Pareto frontier · Sharpe vs Max Drawdown</span>
            <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>{paretoFront.length} non-dominated solutions</span>
          </div>
          {paretoFront.length > 0 ? (
            <>
              <ParetoChart
                paretoFront={paretoFront}
                dominated={topCandidates.filter(c => !paretoFront.some(p => p.objectives && p.objectives[0] === c.raw?.[0]))
                  .map(c => ({ objectives: c.raw || [c.fitness, 0.1] }))}
                height={360}
              />
              <div style={{marginTop:14,fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)",lineHeight:1.55}}>
                Each green point on the frontier represents a non-dominated trade-off — no other tested configuration is strictly better on both Sharpe AND drawdown. Click any point in the chart to load its params.
              </div>
            </>
          ) : (
            <div style={{padding:"32px 0",textAlign:"center",fontFamily:"var(--font-ui)",fontSize:13,color:"var(--muted)"}}>
              Switch the Objective to <strong>Custom weighted</strong> and re-run to compute the Pareto frontier via NSGA-II.
            </div>
          )}
        </div>}

        {resultTab==="parallel"&&<div className="card">
          <div className="card-title">Parallel coordinates · top {Math.min(40, topCandidates.length)} candidates</div>
          {topCandidates.length > 0 && Object.keys(topCandidates[0].params || {}).length > 0 ? (
            <ParallelCoordinatesChart
              candidates={topCandidates.slice(0, 40).map(c => ({
                fitness: c.fitness,
                params: typeof c.params === 'object' && !Array.isArray(c.params)
                        ? c.params
                        : { p0: c.params?.[0], p1: c.params?.[1], p2: c.params?.[2] },
              }))}
              dimensions={Object.keys(topCandidates[0].params || { p0:0, p1:0, p2:0 }).map(k => ({
                label: k, key: k,
                range: [
                  Math.min(...topCandidates.map(c => +c.params?.[k] || 0)),
                  Math.max(...topCandidates.map(c => +c.params?.[k] || 0)),
                ],
              }))}
              height={320}
            />
          ) : (
            <div style={{padding:"32px 0",textAlign:"center",fontFamily:"var(--font-ui)",fontSize:13,color:"var(--muted)"}}>No candidate data yet.</div>
          )}
          <div style={{marginTop:10,fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)"}}>
            Lines coloured by fitness (purple → blue → green). Drag axes to filter to regions where good params concentrate.
          </div>
        </div>}

        {resultTab==="params"&&<div className="card" style={{border:"1px solid rgba(217,70,239,0.2)"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:"var(--primary)",boxShadow:"0 0 8px rgba(217,70,239,0.6)"}}/>
            <span style={{fontFamily:"var(--font-ui)",fontSize:14,fontWeight:600,color:"var(--ink)"}}>Optimal parameters found</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8,marginBottom:14}}>
            {ranges.map(p=>(
              <div key={p.name} style={{background:"var(--primary-dim)",border:"1px solid rgba(217,70,239,0.18)",borderRadius:"var(--r-md)",padding:"8px 10px"}}>
                <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--muted)"}}>{p.name}</div>
                <div style={{fontFamily:"var(--font-mono)",fontSize:15,color:"var(--primary)",marginTop:2,fontWeight:500}}>{p.best}</div>
              </div>
            ))}
          </div>
          {/* Before/After */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,paddingTop:14,borderTop:"1px solid var(--border)"}}>
            <div>
              <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:8}}>BEFORE (default params)</div>
              <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                {(beforeMetrics ? [
                  {l:"Sharpe", v:(beforeMetrics.sharpe||0).toFixed(2)},
                  {l:"CAGR",   v:`+${((beforeMetrics.cagr||0)*100).toFixed(1)}%`},
                  {l:"Max DD", v:`-${Math.abs((beforeMetrics.maxDrawdown||0)*100).toFixed(1)}%`},
                ] : [{l:"Sharpe",v:"—"},{l:"CAGR",v:"—"},{l:"Max DD",v:"—"}]).map(m=>(
                  <div key={m.l}>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:9,color:"var(--subtle)"}}>{m.l}</div>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:14,color:"var(--body)"}}>{m.v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--green)",marginBottom:8}}>AFTER (optimized)</div>
              <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                {(afterMetrics ? [
                  {l:"Sharpe", v:(afterMetrics.sharpe||0).toFixed(2),   c:(afterMetrics.sharpe||0)>=(beforeMetrics?.sharpe||0)?"var(--green)":"var(--red)"},
                  {l:"CAGR",   v:`+${((afterMetrics.cagr||0)*100).toFixed(1)}%`, c:"var(--green)"},
                  {l:"Max DD", v:`-${Math.abs((afterMetrics.maxDrawdown||0)*100).toFixed(1)}%`, c:Math.abs(afterMetrics.maxDrawdown||0)<=Math.abs(beforeMetrics?.maxDrawdown||1)?"var(--green)":"var(--red)"},
                ] : [{l:"Sharpe",v:"—"},{l:"CAGR",v:"—"},{l:"Max DD",v:"—"}]).map(m=>(
                  <div key={m.l}>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:9,color:"var(--subtle)"}}>{m.l}</div>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:14,color:m.c||"var(--ink)",fontWeight:m.c?600:400}}>{m.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>}

        {resultTab==="sensitivity"&&<div className="card">
          <div className="card-title">Parameter sensitivity · ±5/10/20% from optimum (real re-evaluation)</div>
          {sensReport ? (
            <>
              <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:10}}>
                {Object.entries(sensReport.sensitivity).map(([key, perturbs]) => (
                  <div key={key} style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:110,fontFamily:"var(--font-ui)",fontSize:12,color:"var(--ink)",fontWeight:500}}>{key}</div>
                    <div style={{flex:1,display:"flex",gap:4,alignItems:"center"}}>
                      {perturbs.map(p => {
                        const norm = Math.tanh(p.delta / Math.max(0.01, Math.abs(sensReport.baseFitness)));
                        const col = norm > 0 ? '#11ff99' : norm < 0 ? '#f87171' : 'rgba(255,255,255,0.2)';
                        return (
                          <div key={p.pct} title={`${(p.pct*100).toFixed(0)}%: Δfit ${p.delta.toFixed(3)}`} style={{
                            flex:1, height:24,
                            background: col, opacity: 0.3 + Math.abs(norm) * 0.7,
                            borderRadius:3, display:"flex", alignItems:"center", justifyContent:"center",
                            fontFamily:"var(--font-mono)", fontSize:9, color:"rgba(255,255,255,0.85)",
                          }}>{(p.pct*100).toFixed(0)}%</div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginTop:14,lineHeight:1.5}}>
                Real re-evaluation at ±5%, ±10%, ±20% offsets. Green = improvement, red = degradation, faded = stable.
                Base fitness: <strong style={{color:"var(--ink)",fontFamily:"var(--font-mono)"}}>{sensReport.baseFitness.toFixed(3)}</strong>
              </div>
            </>
          ) : (
            <div style={{padding:"32px 0",textAlign:"center",fontFamily:"var(--font-ui)",fontSize:13,color:"var(--muted)"}}>Computing sensitivity grid…</div>
          )}
        </div>}

        {resultTab==="robustness"&&<div className="card">
          <div className="card-title">Robustness · neighbourhood variance (real sample)</div>
          <div style={{display:"flex",alignItems:"center",gap:20,marginBottom:14}}>
            <div style={{width:80,height:80,borderRadius:"50%",background:"var(--primary-dim)",border:"2px solid rgba(217,70,239,0.3)",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column"}}>
              <span style={{fontFamily:"var(--font-mono)",fontSize:24,fontWeight:700,color: robust ? (robust.score > 70 ? "#11ff99" : robust.score > 40 ? "#fbbf24" : "#f87171") : "var(--muted)"}}>
                {robust ? robust.score : '—'}
              </span>
              <span style={{fontFamily:"var(--font-ui)",fontSize:9,color:"var(--muted)"}}>/100</span>
            </div>
            <div style={{flex:1}}>
              <div style={{fontFamily:"var(--font-ui)",fontSize:13,color:"var(--ink)",fontWeight:500,marginBottom:4}}>
                {robust ? (robust.score > 70 ? 'Strong robustness' : robust.score > 40 ? 'Moderate robustness' : 'Fragile — likely overfit') : 'Computing…'}
              </div>
              <div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)",lineHeight:1.5}}>
                {robust ? (
                  <>
                    Sampled <strong style={{color:"var(--ink)"}}>{robust.samples.length}</strong> nearby parameter sets within ±10% of the optimum.
                    Mean fitness: <strong style={{color:"var(--ink)",fontFamily:"var(--font-mono)"}}>{robust.neighbourMean.toFixed(3)}</strong>,
                    std: <strong style={{color:"var(--ink)",fontFamily:"var(--font-mono)"}}>{robust.neighbourStd.toFixed(3)}</strong>.
                    {robust.score < 50 && ' Consider widening the parameter ranges or adding a regularisation term.'}
                  </>
                ) : 'Re-evaluating nearby params to measure how stable the optimum is.'}
              </div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {(robust ? [
              {l:"Neighbour mean fitness", v:robust.neighbourMean.toFixed(3), c:robust.score>70?"var(--green)":robust.score>40?"#fbbf24":"var(--red)"},
              {l:"Neighbour std (stability)", v:robust.neighbourStd.toFixed(3), c:robust.neighbourStd<0.1?"var(--green)":"#fbbf24"},
              {l:"Samples tested", v:String(robust.samples.length), c:"#3b9eff"},
              {l:"PBO score", v:pboScore!=null?`${(pboScore*100).toFixed(0)}%`:"—", c:pboScore==null?"var(--muted)":pboScore>0.5?"var(--red)":pboScore>0.3?"#fbbf24":"var(--green)"},
            ] : [
              {l:"Neighbour mean fitness",v:"—",c:"var(--muted)"},
              {l:"Neighbour std (stability)",v:"—",c:"var(--muted)"},
              {l:"Samples tested",v:"—",c:"var(--muted)"},
              {l:"PBO score",v:"—",c:"var(--muted)"},
            ]).map(m=>(
              <div key={m.l} style={{background:"rgba(255,255,255,0.02)",borderRadius:"var(--r-md)",padding:"10px 12px"}}>
                <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)"}}>{m.l}</div>
                <div style={{fontFamily:"var(--font-mono)",fontSize:13,color:m.c,fontWeight:500,marginTop:2}}>{m.v}</div>
              </div>
            ))}
          </div>
        </div>}

        {resultTab==="pbo"&&<div className="card">
          <div className="card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>Probability of backtest overfitting (PBO)</span>
            {pboScore != null && (
              <span className={`badge ${pboScore > 0.5 ? "badge-red" : pboScore > 0.3 ? "badge-orange" : "badge-green"}`} style={{fontSize:10}}>
                {(pboScore*100).toFixed(0)}% overfit
              </span>
            )}
          </div>
          {pboLoading && (
            <div style={{padding:"32px 0",textAlign:"center",fontFamily:"var(--font-ui)",fontSize:13,color:"var(--muted)"}}>
              <RefreshCw size={16} style={{animation:"spin 1s linear infinite",verticalAlign:"middle",marginRight:8}}/>
              Running 8×8 combinatorial splits — re-evaluating top candidates across time slices…
            </div>
          )}
          {!pboLoading && pboScore == null && (
            <div style={{padding:"32px 0",textAlign:"center",fontFamily:"var(--font-ui)",fontSize:13,color:"var(--muted)"}}>
              PBO is computed automatically after a single-objective run completes.
            </div>
          )}
          {!pboLoading && pboScore != null && (
            <>
              {/* Gauge */}
              <div style={{display:"flex",alignItems:"center",gap:20,marginTop:8,marginBottom:14}}>
                <div style={{position:"relative",width:120,height:120,flexShrink:0}}>
                  <svg viewBox="0 0 120 120" width="120" height="120">
                    <circle cx="60" cy="60" r="48" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10"/>
                    <circle cx="60" cy="60" r="48" fill="none"
                      stroke={pboScore > 0.5 ? "#f87171" : pboScore > 0.3 ? "#fbbf24" : "#11ff99"}
                      strokeWidth="10" strokeLinecap="round"
                      strokeDasharray={`${pboScore * Math.PI * 96} ${Math.PI * 96}`}
                      transform="rotate(-90 60 60)"/>
                    <text x="60" y="64" textAnchor="middle" fontSize="22" fontFamily="var(--font-mono)" fontWeight="700"
                      fill={pboScore > 0.5 ? "#f87171" : pboScore > 0.3 ? "#fbbf24" : "#11ff99"}>
                      {(pboScore*100).toFixed(0)}%
                    </text>
                    <text x="60" y="82" textAnchor="middle" fontSize="9" fontFamily="var(--font-ui)" fill="var(--muted)">PBO</text>
                  </svg>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontFamily:"var(--font-ui)",fontSize:13,color:"var(--ink)",fontWeight:600,marginBottom:4}}>
                    {pboScore > 0.5 ? "Likely overfit — out-of-sample rank degrades >50% of splits"
                      : pboScore > 0.3 ? "Moderate overfit risk"
                      : "Strong out-of-sample stability"}
                  </div>
                  <div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)",lineHeight:1.55}}>
                    Bailey & López de Prado's PBO measures how often the best in-sample candidate falls below the median in held-out time slices.
                    Lower is better; <strong style={{color:"var(--ink)"}}>&lt; 30 %</strong> is the institutional bar.
                    Computed on top 8 candidates × 8 combinatorial time-slice splits.
                  </div>
                </div>
              </div>
              {pboScore > 0.5 && (
                <div style={{
                  background:"rgba(255,32,71,0.06)",border:"1px solid rgba(255,32,71,0.25)",
                  borderRadius:"var(--r-md)",padding:"10px 14px",
                  fontFamily:"var(--font-ui)",fontSize:12,color:"var(--red)",lineHeight:1.55,
                }}>
                  <b>Action:</b> the optimisation has likely fit to noise. Try widening the search ranges, increasing the holdout fraction in walk-forward, or simplifying the strategy spec.
                </div>
              )}
            </>
          )}
        </div>}

        {resultTab==="summary"&&<div className="card" style={{background:"rgba(217,70,239,0.03)",border:"1px solid rgba(217,70,239,0.15)"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <span style={{fontFamily:"'Inter Tight','Inter',sans-serif",fontSize:14,fontWeight:800,color:"#D946EF",letterSpacing:"-0.02em"}}>GQ</span>
            <span style={{fontFamily:"var(--font-ui)",fontSize:13,fontWeight:600,color:"var(--ink)"}}>Optimization Summary</span>
          </div>
          {aiSummary&&<div style={{fontFamily:"var(--font-ui)",fontSize:13,color:"var(--body)",lineHeight:1.7,whiteSpace:"pre-wrap"}}>{aiSummary}</div>}
        </div>}

        {/* Pipeline CTAs */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button className="btn btn-primary btn-sm" onClick={()=>{onToast?.("Applied optimized parameters");}}><Check size={11}/> Apply params</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate?.("stress")}><Shield size={11}/> Stress test →</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate?.("backtest")}><Play size={11}/> Re-backtest</button>
        </div>
      </>}
    </div>
  );
}
window.OptimizePage=OptimizePage;
